import { useState, useMemo } from "react";
import { registerBook } from "../api/books";
import { toGenre } from "../api/recommend";
import { track } from "../utils/analytics";
import LibraryBookDetail from "./LibraryBookDetail";
import "./HotBooks.css";

/* 제목 견주기용. 판본 표기와 띄어쓰기를 지운다. */
const norm = (t) => String(t ?? "").replace(/[([][^)\]]*[)\]]/g, "").replace(/\s+/g, "").toLowerCase();

/**
 * 도서관 정보나루에서 미리 받아 둔 책 목록 — 한 줄을 누르면 그 자리에서
 * 소개가 펼쳐지고, 읽고 싶은 책으로 담을 수 있다.
 * '도서관 대출 급상승 책' 과 '우리 지역 인기 책' 이 함께 쓴다. 둘이 따로
 * 들고 있으면 담기·이미 있는 책 판정이 한쪽만 고쳐진다.
 *
 * @param books    [{ isbn, title, author, cover, cls, desc, ... }]
 * @param side     (b) => 줄 오른쪽에 둘 것 (오름폭, 순위 등)
 * @param uid      나. 없으면(게스트) 담기 버튼을 내지 않는다.
 * @param myBooks  내 행성의 책. 이미 있는 책은 '담기' 대신 '내 행성에 있어요'.
 * @param canAdd   게스트·익명이면 false.
 * @param event    담았을 때 남길 분석 이벤트 이름
 */
export default function LibraryBookList({ books, side, uid = null, myBooks = [], canAdd = false, event = "hotbooks_wish" }) {
  const [adding, setAdding] = useState(null);
  const [added, setAdded] = useState(() => new Set());
  /** 펼쳐 둔 책 하나. 여럿을 동시에 펼치면 목록이 길어져 훑을 수가 없다. */
  const [openIsbn, setOpenIsbn] = useState(null);

  /* 이미 내 행성에 있는 책. ISBN 과 제목 둘 다 본다 — 판본이 달라 ISBN 이
     어긋나도 같은 책을 두 번 담지 않게. */
  const mine = useMemo(() => {
    const isbns = new Set(), titles = new Set();
    for (const m of myBooks || []) {
      if (m.isbn) isbns.add(String(m.isbn));
      titles.add(norm(m.title));
    }
    return { isbns, titles };
  }, [myBooks]);
  const isMine = (b) => mine.isbns.has(String(b.isbn)) || mine.titles.has(norm(b.title));

  const addToWish = async (b) => {
    if (!uid || adding) return;
    setAdding(b.isbn);
    try {
      await registerBook(
        {
          title: b.title,
          author: b.author,
          genre: toGenre(b.cls),
          cover: b.cover || null,
          /* 무루에 이미 있는 책(페이지가 있는 책)에는 소개글을 넘기지 않는다.
             registerBook 은 넘긴 소개글로 덮어쓰는데, 정보나루 소개글은
             '...' 로 잘려 오는 것이 있어 기존 소개글보다 나빠질 수 있다. */
          description: b.hasPage ? null : (b.desc || null),
          isbn: b.isbn || null,
        },
        uid,
        { wantToRead: true }
      );
      setAdded((prev) => new Set(prev).add(b.isbn));
      track(event);
    } catch (e) {
      console.error("읽고 싶은 책 담기 실패:", e?.message);
    } finally {
      setAdding(null);
    }
  };

  return (
    <ul className="hb-list">
      {books.map((b) => {
        const isOpen = openIsbn === b.isbn;
        return (
          <li key={b.isbn} className={isOpen ? "hb-item on" : "hb-item"}>
            <button
              className="hb-row"
              onClick={() => setOpenIsbn(isOpen ? null : b.isbn)}
              aria-expanded={isOpen}
            >
              {b.cover
                ? <img className="hb-cv" src={b.cover} alt="" loading="lazy" />
                : <span className="hb-cv hb-cv-none" aria-hidden="true">📖</span>}
              <span className="hb-txt">
                <b>{b.title}</b>
                <small>{b.author}</small>
              </span>
              {side && side(b)}
            </button>

            {isOpen && (
              <LibraryBookDetail book={b}>
                {/* 담기. 게스트는 담을 행성이 없어 내지 않는다. */}
                {canAdd && uid && (isMine(b) ? (
                  <span className="hb-wish done">내 행성에 있어요</span>
                ) : (
                  <button
                    className={added.has(b.isbn) ? "hb-wish done" : "hb-wish"}
                    onClick={() => addToWish(b)}
                    disabled={adding === b.isbn || added.has(b.isbn)}
                  >
                    {added.has(b.isbn) ? "담았어요" : adding === b.isbn ? "담는 중…" : "🔖 읽고 싶은 책"}
                  </button>
                ))}
                {/* 무루에 페이지가 있는 책에만 단다. 없는 책에 걸면
                    홈으로 떨어져, 누른 사람이 길을 잃는다. */}
                {b.hasPage && (
                  <a className="hb-go" href={`https://muroo.kr/book/${b.isbn}`} target="_blank" rel="noopener noreferrer">
                    무루에서 이 책 보기 →
                  </a>
                )}
              </LibraryBookDetail>
            )}
          </li>
        );
      })}
    </ul>
  );
}
